<script setup lang="ts">
import AppGrid from "@/components/AppGrid.vue";
import { FITTABLE_DEFAULT_COL_DEF } from "@/components/composables/useGridColumnDefaults.ts";
import type { MovementGridRow } from "@/redistribution/models/MovementGridRow.ts";
import type { PurchaseOrder } from "@/redistribution/models/PurchaseOrder.ts";
import type { ColDef } from "ag-grid-community";
import { computed, ref, watch } from "vue";
import { urgencyLabel } from "./resultHelpers.ts";

interface Props {
  isOpen: boolean;
  movement: MovementGridRow | null;
  movements: MovementGridRow[];
  updatedPos: PurchaseOrder[];
}

interface Emits {
  close: [];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

interface PairSkuFlow {
  donorDelta: number;
  recipientDelta: number;
}

type UpdatedColumnState = "updatedDonor" | "updatedRecipient";
const updatedColumnState = ref<UpdatedColumnState>("updatedRecipient");
const showOnlyMovement = ref<boolean>(false);

interface SkuRowData {
  sku: string;
  collection: string;
  description: string;
  color: string;
  qtyOnPo: number;
  cubes: number;
  currentStock: number;
  forecastQty: number;
  dailyDemand: number;
  weeksOfStock: number;
  urgency: number;
}

// Prevent re-entrant scroll handling while applying synced positions.
let isSyncingGridScroll = false;

// Cached viewport elements — populated after mount to avoid per-event DOM queries.
const cachedViewports = new Map<string, HTMLElement>();

function resolveGridViewport(gridId: string): HTMLElement | null {
  const cachedViewport = cachedViewports.get(gridId);
  if (cachedViewport) {
    return cachedViewport;
  }

  const grid = document.querySelector<HTMLElement>(`#${gridId}`);
  const viewport =
    grid?.querySelector<HTMLElement>(".ag-body-viewport") ?? null;
  if (viewport) cachedViewports.set(gridId, viewport);
  return viewport;
}

// Invalidate cache whenever the modal opens so we re-resolve after DOM settles.
watch(
  () => props.isOpen,
  (open) => {
    if (!open) cachedViewports.clear();
  }
);

function getPoSkuRows(poNumber: string): SkuRowData[] {
  const po = props.updatedPos.find((p) => p.purchaseOrderNumber === poNumber);
  if (!po) return [];

  return po.skus.map((sku) => {
    const weeksOfStock = sku.daysToStockout / 7;
    return {
      sku: sku.skuNumber,
      collection: sku.theme,
      description: sku.description,
      color: sku.color,
      qtyOnPo: sku.lineItemQuantity,
      cubes: sku.cubeSize * sku.lineItemQuantity,
      currentStock: sku.quantityAvailable,
      forecastQty: sku.forecastQuantityAvailable,
      dailyDemand: sku.dailyRateOfSale,
      weeksOfStock: Math.round(weeksOfStock * 100) / 100,
      urgency: sku.urgency,
    };
  });
}

const donorRows = computed(() => {
  if (!props.movement) return [];
  return getPoSkuRows(props.movement.from);
});

const recipientRows = computed(() => {
  if (!props.movement) return [];
  return getPoSkuRows(props.movement.to);
});

interface AlignedSkuRow {
  sku: string;
  donor: SkuRowData | undefined;
  recipient: SkuRowData | undefined;
}

const alignedRows = computed<AlignedSkuRow[]>(() => {
  const donorMap = new Map(donorRows.value.map((row) => [row.sku, row]));
  const recipientMap = new Map(
    recipientRows.value.map((row) => [row.sku, row])
  );

  const movementSkus = new Set(
    relatedPairMovements.value.map((movementRow) => movementRow.sku)
  );

  const skuSet = new Set<string>([
    ...donorMap.keys(),
    ...recipientMap.keys(),
    ...movementSkus,
  ]);

  const movementSku = props.movement?.sku;
  const sortedSkus = [...skuSet].toSorted((a, b) => {
    if (movementSku && a === movementSku && b !== movementSku) return -1;
    if (movementSku && b === movementSku && a !== movementSku) return 1;
    return a.localeCompare(b);
  });

  return sortedSkus.map((sku) => ({
    sku,
    donor: donorMap.get(sku),
    recipient: recipientMap.get(sku),
  }));
});

// Filtered rows for display based on "show only movement" toggle
const visibleAlignedRows = computed<AlignedSkuRow[]>(() => {
  if (!showOnlyMovement.value) return alignedRows.value;

  return alignedRows.value.filter((row) => {
    const donorDelta = donorDeltaForSku(row.sku);
    const recipientDelta = recipientDeltaForSku(row.sku);
    return donorDelta !== 0 || recipientDelta !== 0;
  });
});

const donorGridRows = computed<AlignedSkuRow[]>(() =>
  visibleAlignedRows.value.filter((row) => row.donor !== undefined)
);

const recipientGridRows = computed<AlignedSkuRow[]>(() =>
  visibleAlignedRows.value.filter((row) => row.recipient !== undefined)
);

const gridRowsForThirdColumn = computed<AlignedSkuRow[]>(() =>
  visibleAlignedRows.value.filter((row) =>
    isUpdatedRecipientColumn.value
      ? row.recipient !== undefined
      : row.donor !== undefined
  )
);

const relatedPairMovements = computed<MovementGridRow[]>(() => {
  if (!props.movement) return [];

  const fromPo = props.movement.from;
  const toPo = props.movement.to;

  return props.movements.filter(
    (movementRow) =>
      (movementRow.from === fromPo && movementRow.to === toPo) ||
      (movementRow.from === toPo && movementRow.to === fromPo)
  );
});

const pairFlowBySku = computed<Map<string, PairSkuFlow>>(() => {
  if (!props.movement) return new Map();

  const fromPo = props.movement.from;
  const toPo = props.movement.to;
  const flowMap = new Map<string, PairSkuFlow>();

  for (const movementRow of relatedPairMovements.value) {
    const existing = flowMap.get(movementRow.sku) ?? {
      donorDelta: 0,
      recipientDelta: 0,
    };

    if (movementRow.from === fromPo && movementRow.to === toPo) {
      existing.donorDelta -= movementRow.qty;
      existing.recipientDelta += movementRow.qty;
    } else {
      existing.donorDelta += movementRow.qty;
      existing.recipientDelta -= movementRow.qty;
    }

    flowMap.set(movementRow.sku, existing);
  }

  return flowMap;
});

const modalTitle = computed(() => {
  if (!props.movement) return "Movement Detail";
  return `Movement: ${props.movement.sku} from ${props.movement.from} → ${props.movement.to}`;
});

watch(
  () => [
    props.isOpen,
    props.movement?.sku,
    props.movement?.from,
    props.movement?.to,
  ],
  () => {
    if (props.isOpen) {
      updatedColumnState.value = "updatedRecipient";
      showOnlyMovement.value = false;
    }
  }
);

const isUpdatedRecipientColumn = computed(
  () => updatedColumnState.value === "updatedRecipient"
);

const updatedColumnTitle = computed(() =>
  isUpdatedRecipientColumn.value ? "Updated Recipient" : "Updated Donor"
);

function toggleUpdatedColumn(): void {
  updatedColumnState.value = isUpdatedRecipientColumn.value
    ? "updatedDonor"
    : "updatedRecipient";
}

function donorDeltaForSku(sku: string): number {
  return pairFlowBySku.value.get(sku)?.donorDelta ?? 0;
}

function recipientDeltaForSku(sku: string): number {
  return pairFlowBySku.value.get(sku)?.recipientDelta ?? 0;
}

function updatedColumnDeltaForSku(sku: string): number {
  return isUpdatedRecipientColumn.value
    ? recipientDeltaForSku(sku)
    : donorDeltaForSku(sku);
}

function movementPillLabel(delta: number): string {
  if (delta > 0) return `+${String(delta)} received`;
  if (delta < 0) return `${String(delta)} moved`;
  return "";
}

function movementPillClass(delta: number): string {
  return delta >= 0 ? "movement-pill-recipient" : "movement-pill-donor";
}

// Helper to render item details with SKU and movement pill
function renderItemDetailsCell(
  sku: string,
  delta: number,
  showPill: boolean
): HTMLElement {
  const containerEl = document.createElement("div");
  containerEl.className = "item-details-container";

  const skuEl = document.createElement("strong");
  skuEl.textContent = sku;
  containerEl.append(skuEl);

  if (showPill && delta !== 0) {
    const pillEl = document.createElement("span");
    pillEl.className = `movement-pill ${movementPillClass(delta)}`;
    pillEl.textContent = movementPillLabel(delta);
    containerEl.append(pillEl);
  }

  return containerEl;
}

// Helper to render description cell
function renderDescriptionCell(data: SkuRowData | undefined): string {
  if (!data) return "—";
  const description = data.description;
  const collection = data.collection;
  const color = data.color;
  const metadata =
    collection && color
      ? `${collection} (${color})`
      : collection || color || "";

  return description + (metadata ? ` • ${metadata}` : "");
}

// Column definitions for all 3 grids
const defaultColDef: ColDef = {
  ...FITTABLE_DEFAULT_COL_DEF,
  filterParams: {
    maxNumConditions: 5,
  },
  cellClass: (params: { data: { delta?: number } | undefined }): string => {
    const delta = params.data?.delta ?? 0;
    if (delta < 0) return "movement-row--donor-out";
    if (delta > 0) return "movement-row--recipient-in";
    return "";
  },
};

const columnsForAllGrids: ColDef[] = [
  {
    field: "sku",
    headerName: "SKU",
    flex: 0.9,
    minWidth: 65,
    filter: "agTextColumnFilter",
    cellRenderer: (params: {
      value: string | undefined;
      data: { sku?: string; delta?: number } | undefined;
    }): HTMLElement => {
      const sku = params.value ?? params.data?.sku ?? "";
      const delta = params.data?.delta ?? 0;
      return renderItemDetailsCell(sku, delta, true);
    },
  },
  {
    field: "description",
    headerName: "Item Details",
    flex: 1.5,
    minWidth: 90,
    filter: "agTextColumnFilter",
    valueGetter: (params: { data: SkuRowData | undefined }) =>
      renderDescriptionCell(params.data),
    cellRenderer: (params: { value: string }) => params.value,
  },
  {
    field: "qtyOnPo",
    headerName: "QTY on PO",
    flex: 0.7,
    minWidth: 60,
    filter: "agNumberColumnFilter",
    valueFormatter: (params: { data: SkuRowData | undefined }): string =>
      String(params.data?.qtyOnPo ?? "—"),
  },
  {
    field: "cubes",
    headerName: "Cubes",
    flex: 0.6,
    minWidth: 55,
    filter: "agNumberColumnFilter",
    valueFormatter: (params: { data: SkuRowData | undefined }): string =>
      String(params.data?.cubes ?? "—"),
  },
  {
    field: "currentStock",
    headerName: "Current Stock",
    flex: 0.7,
    minWidth: 60,
    filter: "agNumberColumnFilter",
    valueFormatter: (params: { data: SkuRowData | undefined }): string =>
      String(params.data?.currentStock ?? "—"),
  },
  {
    field: "forecastQty",
    headerName: "Forecast Qty",
    flex: 0.7,
    minWidth: 60,
    filter: "agNumberColumnFilter",
    valueFormatter: (params: { data: SkuRowData | undefined }): string =>
      String(params.data?.forecastQty ?? "—"),
  },
  {
    field: "dailyDemand",
    headerName: "Daily Demand",
    flex: 0.7,
    minWidth: 60,
    filter: "agNumberColumnFilter",
    valueFormatter: (params: { data: SkuRowData | undefined }): string => {
      const value = params.data?.dailyDemand;
      return typeof value === "number" && Number.isFinite(value)
        ? value.toFixed(2)
        : "—";
    },
  },
  {
    field: "weeksOfStock",
    headerName: "Weeks Stock",
    flex: 0.7,
    minWidth: 60,
    filter: "agNumberColumnFilter",
    valueFormatter: (params: { data: SkuRowData | undefined }): string =>
      String(params.data?.weeksOfStock ?? "—"),
  },
  {
    field: "urgency",
    headerName: "Urgency",
    flex: 0.7,
    minWidth: 60,
    filter: "agTextColumnFilter",
    valueFormatter: (params: { data: SkuRowData | undefined }) =>
      params.data
        ? urgencyLabel(params.data.urgency, params.data.dailyDemand).text
        : "—",
  },
];

// Sync scrolling implementation for the three detail grids.
function syncGridScrolls(
  sourceGridId: string,
  scrollTop: number,
  scrollLeft: number
): void {
  if (isSyncingGridScroll) {
    return;
  }

  isSyncingGridScroll = true;

  const allGridIds = ["donor-grid", "recipient-grid", "updated-grid"];

  // Sync other grids
  for (const id of allGridIds) {
    if (id === sourceGridId) {
      continue;
    }
    const scrollContainer = resolveGridViewport(id);
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollTop;
      scrollContainer.scrollLeft = scrollLeft;
    }
  }

  setTimeout(() => {
    isSyncingGridScroll = false;
  }, 0);
}

function onGridBodyScroll(
  sourceGridId: string,
  payload: { left: number; top: number }
): void {
  syncGridScrolls(sourceGridId, payload.top, payload.left);
}
</script>

<template>
  <div
    v-if="isOpen && movement"
    class="movement-detail-overlay"
    role="dialog"
    aria-modal="true"
    :aria-label="modalTitle"
  >
    <div class="movement-detail-window">
      <header class="movement-detail-header">
        <h3>{{ modalTitle }}</h3>
        <button
          type="button"
          class="movement-detail-close"
          aria-label="Close"
          @click="emit('close')"
        >
          ×
        </button>
      </header>

      <div class="movement-detail-modal">
        <div class="movement-column-controls">
          <div class="movement-column-toggle">
            <span class="movement-column-toggle-label">Third column:</span>
            <button
              type="button"
              class="panel-secondary-button movement-column-toggle-button"
              data-testid="updated-column-toggle"
              :aria-pressed="isUpdatedRecipientColumn"
              @click="toggleUpdatedColumn"
            >
              Show
              {{
                isUpdatedRecipientColumn ? "Updated Donor" : "Updated Recipient"
              }}
            </button>
          </div>

          <div class="movement-filter-toggle">
            <label class="movement-filter-label" for="show-only-movement">
              <input
                id="show-only-movement"
                v-model="showOnlyMovement"
                type="checkbox"
                class="movement-filter-checkbox"
              />
              <span class="movement-filter-text"
                >Show only SKUs with movement</span
              >
            </label>
          </div>
        </div>

        <div class="movement-columns" data-testid="movement-columns">
          <!-- Donor Column -->
          <div
            class="movement-column donor-column"
            data-testid="movement-column-donor"
          >
            <h4>{{ movement.from }} (Donor)</h4>
            <AppGrid
              v-if="donorGridRows.length > 0"
              id="donor-grid"
              :column-defs="columnsForAllGrids"
              :row-data="
                donorGridRows.map((row) => ({
                  ...row.donor,
                  sku: row.sku,
                  delta: donorDeltaForSku(row.sku),
                }))
              "
              :default-col-def="defaultColDef"
              dom-layout="normal"
              :suppress-column-filter="false"
              :enable-column-menu="true"
              :fill-container="false"
              @body-scroll="onGridBodyScroll('donor-grid', $event)"
            />
            <div v-else class="empty-message">
              {{
                showOnlyMovement
                  ? "No SKUs with movement on this PO"
                  : "No SKUs found for this PO"
              }}
            </div>
          </div>

          <!-- Recipient Column -->
          <div
            class="movement-column recipient-column"
            data-testid="movement-column-recipient"
          >
            <h4>{{ movement.to }} (Recipient)</h4>
            <AppGrid
              v-if="recipientGridRows.length > 0"
              id="recipient-grid"
              :column-defs="columnsForAllGrids"
              :row-data="
                recipientGridRows.map((row) => ({
                  ...row.recipient,
                  sku: row.sku,
                  delta: recipientDeltaForSku(row.sku),
                }))
              "
              :default-col-def="defaultColDef"
              dom-layout="normal"
              :suppress-column-filter="false"
              :enable-column-menu="true"
              :fill-container="false"
              @body-scroll="onGridBodyScroll('recipient-grid', $event)"
            />
            <div v-else class="empty-message">
              {{
                showOnlyMovement
                  ? "No SKUs with movement on this PO"
                  : "No SKUs found for this PO"
              }}
            </div>
          </div>

          <!-- Updated Column -->
          <div
            class="movement-column updated-column"
            data-testid="movement-column-updated"
          >
            <h4 data-testid="updated-column-title">
              {{ updatedColumnTitle }}
            </h4>
            <AppGrid
              v-if="gridRowsForThirdColumn.length > 0"
              id="updated-grid"
              :column-defs="columnsForAllGrids"
              :row-data="
                gridRowsForThirdColumn.map((row) => ({
                  ...(isUpdatedRecipientColumn ? row.recipient : row.donor),
                  sku: row.sku,
                  delta: updatedColumnDeltaForSku(row.sku),
                }))
              "
              :default-col-def="defaultColDef"
              dom-layout="normal"
              :suppress-column-filter="false"
              :enable-column-menu="true"
              :fill-container="false"
              @body-scroll="onGridBodyScroll('updated-grid', $event)"
            />
            <div v-else class="empty-message">No SKUs on this PO</div>
          </div>
        </div>

        <div class="movement-summary">
          <strong>Moved:</strong> {{ movement.qty }} units |
          <strong>Reason:</strong> {{ movement.reason }}
        </div>
      </div>

      <footer class="movement-detail-footer">
        <button
          type="button"
          class="panel-secondary-button"
          @click="emit('close')"
        >
          Close
        </button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.movement-detail-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(15 23 42 / 45%);
  padding: 1rem;
}

.movement-detail-window {
  width: min(96vw, 1440px);
  max-height: 90vh;
  display: grid;
  grid-template-rows: auto 1fr auto;
  background: var(--dashboard-bg-primary, #fff);
  border-radius: 0.5rem;
  box-shadow: 0 20px 60px rgb(0 0 0 / 25%);
  border: 1px solid var(--dashboard-border-default, #e5e7eb);
  overflow: hidden;
}

.movement-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--dashboard-border-default, #e5e7eb);
}

.movement-detail-header h3 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--dashboard-text-primary, #1a1a1a);
}

.movement-detail-close {
  border: none;
  background: transparent;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  color: var(--dashboard-text-muted, #64748b);
}

.movement-detail-modal {
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 1rem;
  min-height: 0;
  padding: 1rem;
}

.movement-column-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;
  flex-wrap: wrap;
}

.movement-column-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.movement-column-toggle-label {
  font-size: 12px;
  color: var(--dashboard-text-muted, #64748b);
}

.movement-column-toggle-button {
  padding: 0.25rem 0.625rem;
}

.movement-filter-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.movement-filter-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 12px;
  color: var(--dashboard-text-muted, #64748b);
  cursor: pointer;
  user-select: none;
}

.movement-filter-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--dashboard-accent, #3b82f6);
}

.movement-filter-text {
  cursor: pointer;
}

.movement-columns {
  display: grid;
  grid-template-columns: repeat(3, minmax(22rem, 1fr));
  gap: 1rem;
  min-height: 0;
  overflow: auto hidden;
  padding-bottom: 0.5rem;
}

.movement-column {
  min-width: 0;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--dashboard-border-default, #e5e7eb);
  border-right: 1px solid var(--dashboard-border-default, #e5e7eb);
  padding: 0 0.75rem;
}

.donor-column h4,
.recipient-column h4,
.updated-column h4 {
  margin: 0 0 0.75rem;
  font-size: 14px;
  font-weight: 700;
  color: var(--dashboard-text-primary, #1a1a1a);
}

.empty-message {
  padding: 1rem;
  text-align: center;
  font-size: 12px;
  color: var(--dashboard-text-muted, #9ca3af);
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.movement-summary {
  border-top: 1px solid var(--dashboard-border-default, #e5e7eb);
  padding-top: 0.75rem;
  font-size: 12px;
  color: var(--dashboard-text-primary, #1a1a1a);
}

.movement-detail-footer {
  display: flex;
  justify-content: flex-end;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--dashboard-border-default, #e5e7eb);
}

/* movement-pill elements are injected via cellRenderer (document.createElement),
   so they live outside scoped attribute boundaries — use :deep() to reach them. */
/* stylelint-disable-next-line selector-pseudo-class-no-unknown */
:deep(.item-details-container) {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  overflow: hidden;
}

/* stylelint-disable-next-line selector-pseudo-class-no-unknown */
:deep(.movement-pill) {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.4rem;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}

/* stylelint-disable-next-line selector-pseudo-class-no-unknown */
:deep(.movement-pill-donor) {
  background-color: rgb(220 38 38 / 15%);
  color: rgb(185 28 28);
}

/* stylelint-disable-next-line selector-pseudo-class-no-unknown */
:deep(.movement-pill-recipient) {
  background-color: rgb(22 163 74 / 15%);
  color: rgb(21 128 61);
}

@media (width <= 1100px) {
  .movement-detail-window {
    width: min(98vw, 1440px);
  }

  .movement-columns {
    grid-template-columns: 1fr;
    overflow: hidden auto;
  }

  .movement-column-controls {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
